/**
 * PROTECTED ROUTE COMPONENT
 *
 * Handles route protection and license verification
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { CircularProgress, Box, Alert, Button } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Development bypass (remove in production)
  const isDevelopment = process.env.NODE_ENV === "development";
  console.log("🛡️ ProtectedRoute Check:");
  console.log("- Environment:", process.env.NODE_ENV);
  console.log("- isAuthenticated:", isAuthenticated);
  console.log("- isLoading:", isLoading);
  console.log("- user:", user);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log("🚫 Not authenticated, redirecting to login");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for user data if authenticated but user is null
  if (isAuthenticated && !user) {
    console.log("⏳ Authenticated but user data not loaded yet");
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Check license validity - More comprehensive check
  if (user) {
    console.log("🔍 ProtectedRoute License Check:");
    console.log("User data:", user);
    console.log("Raw user object:", JSON.stringify(user, null, 2));
    console.log("isValid:", user.isValid);
    console.log("isExpired:", user.isExpired);
    console.log("daysRemaining:", user.daysRemaining);

    // Temporary fix - force valid license for testing
    const FORCE_VALID_LICENSE = true; // Change to false in production

    if (FORCE_VALID_LICENSE) {
      console.log(
        "🚀 FORCE_VALID_LICENSE is enabled - bypassing license check",
      );
    } else {
      // License is invalid if either isValid is false OR isExpired is true
      const isLicenseInvalid = !user.isValid || user.isExpired;

      console.log("License invalid?", isLicenseInvalid);

      if (isLicenseInvalid) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bgcolor="background.default"
            p={3}
          >
            <Alert
              severity="error"
              sx={{ maxWidth: 400 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => (window.location.href = "/license")}
                >
                  UPGRADE
                </Button>
              }
            >
              Your license has expired. Please upgrade to continue using the
              bot.
              <br />
              <small>
                Debug: isValid={user.isValid ? "true" : "false"}, isExpired=
                {user.isExpired ? "true" : "false"}
              </small>
            </Alert>
          </Box>
        );
      }
    }
  }

  // Render protected content
  return children;
};

export default ProtectedRoute;
